import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import {
  generateLoginRoute,
  generateTrialSignupRoute,
  generateResendEmailRoute,
  generateRoute,
  EAppRoutes,
} from "@router/router-helper";
import { generateSearchParamsQuery } from "@utils/url-utils";

interface UseAppNavigation {
  /** Navigates to login */
  navigateToLogin(): void;

  /** Navigates to signup trial*/
  navigateToTrialSignup(): void;

  /** Navigates to resend verification email page */
  navigateToResendEmail(): void;

  /** Navigate to a specific route */
  navigateToRoute(options: {
    route: EAppRoutes;
    additionalQuery?: Record<string, string>;
  }): void;
}

/**
 * A hook that gathers all the navigation and routing in the app
 */
export function useAppNavigation(): UseAppNavigation {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  /**
   * Gets the search parameters from the URL.
   * It checks if the URL contains a hash and extracts the search parameters from it.
   * If no hash is present, it defaults to the current search parameters.
   */
  function getSearchParams(options?: {
    additionalQuery?: Record<string, string>;
  }): string {
    return generateSearchParamsQuery({
      searchParams,
      options,
      hash: window.location.hash,
    });
  }

  function navigateToLogin(): void {
    void navigate({
      pathname: generateLoginRoute(),
      search: getSearchParams(),
    });
  }

  function navigateToTrialSignup(): void {
    void navigate({
      pathname: generateTrialSignupRoute(),
      search: getSearchParams(),
    });
  }

  function navigateToResendEmail(): void {
    void navigate({
      pathname: generateResendEmailRoute(),
      search: getSearchParams(),
    });
  }

  /**
   * Navigates to an specific route and includes some optional query parameters.
   */
  function navigateToRoute({
    route,
    additionalQuery,
  }: {
    route: EAppRoutes;
    additionalQuery?: Record<string, string>;
  }): void {
    void navigate({
      pathname: generateRoute(route),
      search: getSearchParams({
        additionalQuery,
      }),
    });
  }

  return {
    navigateToLogin,
    navigateToTrialSignup,
    navigateToResendEmail,
    navigateToRoute,
  };
}
